import { useEffect, useRef, useState } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import {
  startToolOperation,
  trackToolRejection,
  type ToolAnalyticsContext,
  type ToolOperation,
} from '@saas-pages/shared/public/runtime'
import { useT } from '@/shared/i18n'
import { getUtilityDefinition } from '@/shared/utilities/utilities'
import { ACROFORM_LIMITS } from '@/features/acroform-filler/model/limits'
import type {
  AcroField,
  AcroFieldValue,
  FillerErrorCode,
} from '@/features/acroform-filler/model/types'
import { AcroFormWorkerClient } from '@/features/acroform-filler/model/workerClient'
import { downloadPdfBlob } from '@/features/acroform-filler/model/download'

const DEFINITION = getUtilityDefinition('fill-pdf-form')
const ANALYTICS = {
  app: 'pdfy',
  domain: 'pdfy.ru',
  route: DEFINITION.path,
  utility: DEFINITION.analyticsKey,
  processingMode: 'local',
} satisfies ToolAnalyticsContext

type Phase =
  | { kind: 'idle' }
  | { kind: 'inspecting'; file: File }
  | { kind: 'editing'; file: File; fields: AcroField[]; pageCount: number }
  | { kind: 'saving'; file: File; fields: AcroField[]; pageCount: number }
  | { kind: 'error'; code: FillerErrorCode }

function outputName(name: string, flattened: boolean): string {
  const base = name.toLowerCase().endsWith('.pdf') ? name.slice(0, -4) : name
  return `${base}-${flattened ? 'flattened' : 'filled'}.pdf`
}

function analyticsCode(code: FillerErrorCode) {
  if (code === 'limit') return 'size_limit' as const
  if (code === 'invalid-pdf') return 'invalid_input' as const
  if (code === 'processing') return 'processing_failed' as const
  return 'unsupported_input' as const
}

interface FieldControlProps {
  field: AcroField
  value: AcroFieldValue
  disabled: boolean
  onChange: (value: AcroFieldValue) => void
}

function FieldControl({ field, value, disabled, onChange }: FieldControlProps) {
  const inputClass =
    'w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring disabled:opacity-60'

  if (field.kind === 'checkbox') {
    return (
      <input
        type="checkbox"
        checked={value === true}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        className="h-5 w-5 rounded border-input accent-primary"
      />
    )
  }
  if (field.kind === 'radio') {
    return (
      <div className="flex flex-wrap gap-x-4 gap-y-2">
        {field.options.map((option) => (
          <label key={option.exportValue} className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name={`acro-${field.id}`}
              value={option.exportValue}
              checked={value === option.exportValue}
              disabled={disabled}
              onChange={() => onChange(option.exportValue)}
              className="accent-primary"
            />
            <span className="break-all">{option.label}</span>
          </label>
        ))}
      </div>
    )
  }
  if (field.kind === 'dropdown') {
    return (
      <select
        value={typeof value === 'string' ? value : ''}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        className={inputClass}
      >
        <option value="" />
        {field.options.map((option) => (
          <option key={option.exportValue} value={option.exportValue}>
            {option.label}
          </option>
        ))}
      </select>
    )
  }
  if (field.kind === 'option-list') {
    const selected = Array.isArray(value) ? value : []
    return (
      <select
        multiple={field.multiselect}
        value={field.multiselect ? selected : (selected[0] ?? (typeof value === 'string' ? value : ''))}
        disabled={disabled}
        onChange={(event) =>
          onChange(
            field.multiselect
              ? Array.from(event.target.selectedOptions, (option) => option.value)
              : event.target.value,
          )
        }
        className={cn(inputClass, 'min-h-28')}
      >
        {field.options.map((option) => (
          <option key={option.exportValue} value={option.exportValue}>
            {option.label}
          </option>
        ))}
      </select>
    )
  }
  return (
    <input
      type="text"
      value={typeof value === 'string' ? value : ''}
      maxLength={ACROFORM_LIMITS.valueChars}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value)}
      className={inputClass}
    />
  )
}

export function AcroFormFiller() {
  const t = useT()
  const copy = t.fillPdfForm
  const workerRef = useRef(new AcroFormWorkerClient())
  const operationRef = useRef<ToolOperation | null>(null)
  const generationRef = useRef(0)
  const startedAtRef = useRef(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' })
  const [values, setValues] = useState<Record<string, AcroFieldValue>>({})
  const [flatten, setFlatten] = useState(false)
  const [dragging, setDragging] = useState(false)

  function cancel(): void {
    generationRef.current += 1
    workerRef.current.cancel()
    operationRef.current = null
  }

  useEffect(() => cancel, [])

  function reset(): void {
    cancel()
    setValues({})
    setFlatten(false)
    setPhase({ kind: 'idle' })
    if (inputRef.current) inputRef.current.value = ''
  }

  async function acceptFile(file: File): Promise<void> {
    cancel()
    const generation = generationRef.current
    const validType =
      file.type === 'application/pdf' || (file.type === '' && file.name.toLowerCase().endsWith('.pdf'))
    if (!validType) {
      trackToolRejection(ANALYTICS, 'unsupported_input')
      setPhase({ kind: 'error', code: 'invalid-pdf' })
      return
    }
    if (file.size === 0 || file.size > ACROFORM_LIMITS.fileBytes) {
      trackToolRejection(ANALYTICS, 'size_limit')
      setPhase({ kind: 'error', code: 'limit' })
      return
    }

    operationRef.current = startToolOperation(ANALYTICS)
    startedAtRef.current = performance.now()
    setPhase({ kind: 'inspecting', file })
    try {
      const bytes = await file.arrayBuffer()
      if (generation !== generationRef.current) return
      const result = await workerRef.current.inspect(bytes)
      if (generation !== generationRef.current) return
      setValues(Object.fromEntries(result.fields.map((field) => [field.id, field.value])))
      setPhase({ kind: 'editing', file, fields: result.fields, pageCount: result.pageCount })
    } catch (error: unknown) {
      if (generation !== generationRef.current) return
      const code = typeof error === 'string' ? (error as FillerErrorCode) : 'processing'
      operationRef.current?.error(analyticsCode(code))
      operationRef.current = null
      setPhase({ kind: 'error', code })
    }
  }

  async function save(): Promise<void> {
    if (phase.kind !== 'editing') return
    const current = phase
    const generation = generationRef.current
    setPhase({ ...current, kind: 'saving' })
    try {
      const bytes = await current.file.arrayBuffer()
      if (generation !== generationRef.current) return
      const output = await workerRef.current.fill(bytes, values, flatten)
      if (generation !== generationRef.current) return
      const magic = new TextDecoder('latin1').decode(new Uint8Array(output, 0, Math.min(5, output.byteLength)))
      if (magic !== '%PDF-') throw new Error('Unexpected PDF output')
      const blob = new Blob([output], { type: 'application/pdf' })
      downloadPdfBlob(blob, outputName(current.file.name, flatten))
      operationRef.current?.success(performance.now() - startedAtRef.current)
      operationRef.current = null
      setPhase({ ...current, kind: 'editing' })
    } catch (error: unknown) {
      if (generation !== generationRef.current) return
      const code = typeof error === 'string' ? (error as FillerErrorCode) : 'processing'
      operationRef.current?.error(analyticsCode(code))
      operationRef.current = null
      setPhase({ kind: 'error', code })
    }
  }

  const isBusy = phase.kind === 'inspecting' || phase.kind === 'saving'
  const currentFile =
    phase.kind === 'inspecting' || phase.kind === 'editing' || phase.kind === 'saving'
      ? phase.file
      : null

  return (
    <div className="space-y-5">
      <div
        role="button"
        tabIndex={isBusy ? -1 : 0}
        aria-label={copy.pick}
        onClick={() => !isBusy && inputRef.current?.click()}
        onKeyDown={(event) => {
          if (!isBusy && (event.key === 'Enter' || event.key === ' ')) inputRef.current?.click()
        }}
        onDragOver={(event) => {
          event.preventDefault()
          if (!isBusy) setDragging(true)
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault()
          setDragging(false)
          const file = event.dataTransfer.files[0]
          if (file && !isBusy) void acceptFile(file)
        }}
        className={cn(
          'rounded-xl border-2 border-dashed p-6 text-center transition-colors sm:p-8',
          dragging ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50',
          isBusy && 'pointer-events-none opacity-60',
        )}
      >
        <p className="font-medium text-foreground">{currentFile?.name ?? copy.pick}</p>
        <p className="mt-1 text-xs text-muted-foreground">{copy.limit}</p>
        <p className="mt-2 text-xs font-medium text-primary">{copy.local}</p>
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,application/pdf"
          className="sr-only"
          onChange={(event) => {
            const file = event.target.files?.[0]
            if (file) void acceptFile(file)
          }}
        />
      </div>

      <Card className="bg-accent/20">
        <CardContent className="space-y-1 p-4 text-sm text-muted-foreground">
          <p className="font-medium text-foreground">{copy.scopeTitle}</p>
          <p>{copy.scope}</p>
        </CardContent>
      </Card>

      {phase.kind === 'error' && (
        <Card className="border-destructive/30 bg-destructive/5">
          <CardContent className="space-y-3 p-4">
            <p role="alert" className="text-sm text-destructive">
              {copy.errors[phase.code]}
            </p>
            <Button variant="outline" onClick={reset}>
              {copy.again}
            </Button>
          </CardContent>
        </Card>
      )}

      {phase.kind === 'inspecting' && (
        <p role="status" className="text-center text-sm text-muted-foreground">
          {copy.inspecting}
        </p>
      )}

      {(phase.kind === 'editing' || phase.kind === 'saving') && (
        <>
          <div className="flex flex-col gap-2 rounded-lg border bg-card p-4 text-sm sm:flex-row sm:items-center sm:justify-between">
            <span>{copy.summary.replace('{pages}', String(phase.pageCount)).replace('{fields}', String(phase.fields.length))}</span>
            <Button variant="outline" size="sm" onClick={reset} disabled={isBusy}>
              {copy.changeFile}
            </Button>
          </div>
          <div className="grid gap-3">
            {phase.fields.map((field, index) => (
              <Card key={field.id}>
                <CardContent className="space-y-2 p-4">
                  <label className="block break-all text-sm font-medium text-foreground">
                    {index + 1}. {field.name}
                  </label>
                  <p className="text-xs text-muted-foreground">{copy.types[field.kind]}</p>
                  <FieldControl
                    field={field}
                    value={values[field.id] ?? field.value}
                    disabled={isBusy}
                    onChange={(value) =>
                      setValues((current) => ({ ...current, [field.id]: value }))
                    }
                  />
                </CardContent>
              </Card>
            ))}
          </div>
          <label className="flex items-start gap-3 rounded-lg border bg-card p-4 text-sm">
            <input
              type="checkbox"
              checked={flatten}
              disabled={isBusy}
              onChange={(event) => setFlatten(event.target.checked)}
              className="mt-0.5 h-4 w-4 accent-primary"
            />
            <span>
              <span className="block font-medium text-foreground">{copy.flatten}</span>
              <span className="text-muted-foreground">{copy.flattenHint}</span>
            </span>
          </label>
          <Button size="lg" className="w-full" disabled={isBusy} onClick={() => void save()}>
            {phase.kind === 'saving' ? copy.saving : copy.download}
          </Button>
        </>
      )}
    </div>
  )
}
